import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  HiUser,
  HiLogout,
  HiShieldCheck,
  HiUserGroup,
  HiHeart,
  HiLink,
  HiDocumentText,
  HiSparkles,
  HiPhone,
  HiBookOpen,
} from 'react-icons/hi';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../hooks/useRBAC';
import { useProfile } from '../contexts/ProfileContext';
import { Link } from 'react-router-dom';
import { ROLE_LABELS } from '../types/rbac';

export default function UserMenu() {
  const { user, signOut } = useAuth();
  const { profile } = useProfile();
  const { isAdmin, isEditor, isModerator, role, can } = useRBAC();
  const avatarUrl = profile?.profile_image_url || user?.user_metadata?.avatar_url;
  const displayName =
    profile?.display_name?.trim() ||
    user?.user_metadata?.full_name ||
    user?.email?.split('@')[0] ||
    'کاربر';

  if (!user) return null;

  const hasAnyAdminAccess = isAdmin || isEditor || isModerator;

  return (
    <Menu as="div" className="relative inline-block text-right">
      <Menu.Button className="relative inline-flex items-center justify-center rounded-full w-9 h-9 bg-zinc-900 text-white hover:bg-zinc-800 transition-all border border-amber-500/40 hover:border-amber-400 overflow-hidden shadow-md shadow-amber-500/10" title={displayName}>
        {avatarUrl ? (
          <img
            src={avatarUrl}
            alt={displayName}
            className="w-full h-full rounded-full object-cover"
          />
        ) : (
          <HiUser className="w-5 h-5 text-amber-400" />
        )}
      </Menu.Button>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute ltr:right-0 ltr:origin-top-right rtl:left-0 rtl:origin-top-left mt-2 w-64 max-w-[calc(100vw-2rem)] rounded-2xl bg-black/95 border border-white/15 shadow-2xl backdrop-blur-xl focus:outline-none z-50">
          <div className="p-2">
            <div className="px-3 py-2.5 text-sm text-gray-300 border-b border-white/10 mb-1">
              <div className="font-bold text-white truncate">{displayName}</div>
              <div className="text-xs text-gray-400 font-mono truncate" dir="ltr">
                {user.email}
              </div>
              {hasAnyAdminAccess && (
                <div className="mt-1.5">
                  <span
                    className={`inline-block text-[10px] font-bold px-2 py-0.5 rounded-md bg-gradient-to-r ${
                      ROLE_LABELS[role]?.color || 'from-primary to-amber-500 text-black'
                    }`}
                  >
                    {ROLE_LABELS[role]?.title || 'مدیر'}
                  </span>
                </div>
              )}
            </div>

            {/* Admin & Editor Management Links */}
            {hasAnyAdminAccess && (
              <>
                {can('can_manage_ai') && (
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/admin/ai-agents"
                        className={`${
                          active ? 'bg-primary/20 text-primary' : 'text-white'
                        } group flex w-full items-center rounded-xl px-3 py-2 text-xs font-medium gap-2 transition-colors`}
                      >
                        <HiSparkles className="w-4 h-4 text-primary" />
                        ایجنت‌های هوش مصنوعی
                      </Link>
                    )}
                  </Menu.Item>
                )}

                {can('can_manage_roles') && (
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/admin/roles"
                        className={`${
                          active ? 'bg-primary/20 text-primary' : 'text-white'
                        } group flex w-full items-center rounded-xl px-3 py-2 text-xs font-medium gap-2 transition-colors`}
                      >
                        <HiShieldCheck className="w-4 h-4 text-amber-400" />
                        مدیریت نقش‌ها (RBAC)
                      </Link>
                    )}
                  </Menu.Item>
                )}

                {can('can_manage_contacts') && (
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/admin/contact-links"
                        className={`${
                          active ? 'bg-primary/20 text-primary' : 'text-white'
                        } group flex w-full items-center rounded-xl px-3 py-2 text-xs font-medium gap-2 transition-colors`}
                      >
                        <HiPhone className="w-4 h-4 text-sky-400" />
                        مدیریت راه‌های ارتباطی
                      </Link>
                    )}
                  </Menu.Item>
                )}

                {can('can_manage_blog') && (
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/admin/blog"
                        className={`${
                          active ? 'bg-primary/20 text-primary' : 'text-white'
                        } group flex w-full items-center rounded-xl px-3 py-2 text-xs font-medium gap-2 transition-colors`}
                      >
                        <HiBookOpen className="w-4 h-4 text-blue-400" />
                        مدیریت مقالات بلاگ
                      </Link>
                    )}
                  </Menu.Item>
                )}

                {can('can_manage_comments') && (
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/admin/comments"
                        className={`${
                          active ? 'bg-primary/20 text-primary' : 'text-white'
                        } group flex w-full items-center rounded-xl px-3 py-2 text-xs font-medium gap-2 transition-colors`}
                      >
                        <HiShieldCheck className="w-4 h-4 text-emerald-400" />
                        نظارت بر کامنت‌ها
                      </Link>
                    )}
                  </Menu.Item>
                )}

                {can('can_manage_users') && (
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/admin/users"
                        className={`${
                          active ? 'bg-primary/20 text-primary' : 'text-white'
                        } group flex w-full items-center rounded-xl px-3 py-2 text-xs font-medium gap-2 transition-colors`}
                      >
                        <HiUserGroup className="w-4 h-4 text-purple-400" />
                        مدیریت کاربران
                      </Link>
                    )}
                  </Menu.Item>
                )}

                {can('can_manage_downloads') && (
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/admin/download-links"
                        className={`${
                          active ? 'bg-primary/20 text-primary' : 'text-white'
                        } group flex w-full items-center rounded-xl px-3 py-2 text-xs font-medium gap-2 transition-colors`}
                      >
                        <HiLink className="w-4 h-4 text-orange-400" />
                        مدیریت لینک‌های دانلود
                      </Link>
                    )}
                  </Menu.Item>
                )}

                {can('can_manage_terms') && (
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/admin/terms"
                        className={`${
                          active ? 'bg-primary/20 text-primary' : 'text-white'
                        } group flex w-full items-center rounded-xl px-3 py-2 text-xs font-medium gap-2 transition-colors`}
                      >
                        <HiDocumentText className="w-4 h-4 text-gray-400" />
                        قوانین و مقررات
                      </Link>
                    )}
                  </Menu.Item>
                )}

                <div className="my-1.5 border-t border-white/10"></div>
              </>
            )}

            {/* User Profile Links */}
            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/profile"
                  className={`${
                    active ? 'bg-white/10' : ''
                  } group flex w-full items-center rounded-xl px-3 py-2 text-xs font-medium text-white gap-2 transition-colors`}
                >
                  <HiUser className="w-4 h-4" />
                  پروفایل من
                </Link>
              )}
            </Menu.Item>

            <Menu.Item>
              {({ active }) => (
                <Link
                  to="/my-games"
                  className={`${
                    active ? 'bg-white/10' : ''
                  } group flex w-full items-center rounded-xl px-3 py-2 text-xs font-medium text-white gap-2 transition-colors`}
                >
                  <HiHeart className="w-4 h-4 text-rose-500" />
                  بازی‌های من
                </Link>
              )}
            </Menu.Item>

            <div className="my-1 border-t border-white/10"></div>

            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={() => signOut()}
                  className={`${
                    active ? 'bg-red-950/60 text-red-300' : 'text-gray-400 hover:text-white'
                  } group flex w-full items-center rounded-xl px-3 py-2 text-xs font-medium gap-2 transition-colors`}
                >
                  <HiLogout className="w-4 h-4 text-red-400" />
                  خروج از حساب
                </button>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
